import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { ServiceIcon } from "@/components/site/icons";
import { ContactCTA } from "@/pages/Home";
import { Check, ArrowUpRight, ArrowLeft } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [others, setOthers] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => setNotFound(true));
    api.get("/services").then((r) => setOthers(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-wv flex min-h-[70vh] flex-col items-center justify-center text-center">
        <h1 className="font-heading text-3xl font-semibold text-white">Service not found</h1>
        <Link to="/services" className="btn-primary mt-6">Back to services</Link>
      </div>
    );
  }
  if (!service) return <div className="min-h-[70vh]" />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.short_desc,
    provider: { "@type": "Organization", name: "Web Vantage Enterprises" },
  };

  return (
    <>
      <SEO title={service.title} description={service.short_desc} path={`/services/${slug}`} schema={schema} />
      <PageHero
        overline={service.category}
        title={service.title}
        subtitle={service.short_desc}
        crumbs={[{ label: "Services", to: "/services" }, { label: service.title }]}
      />
      <section className="py-20 lg:py-28">
        <div className="container-wv grid gap-14 lg:grid-cols-[1.4fr_1fr]">
          <Reveal>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-vantage/10 text-vantage">
              <ServiceIcon name={service.icon} className="h-7 w-7" />
            </div>
            <h2 className="mt-8 font-heading text-3xl font-semibold text-white">The Web Vantage approach</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">{service.description}</p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {service.features?.map((f) => (
                <div key={f} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-vantage" />
                  <span className="text-sm text-slate-200">{f}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="sticky top-28 rounded-3xl border border-white/10 bg-white/[0.03] p-8">
              <h3 className="font-heading text-xl font-semibold text-white">Ready to get started?</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">Book a free consultation and we'll map out a plan tailored to your goals.</p>
              <Link to="/contact" className="btn-primary mt-6 w-full group">
                Start your project
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <div className="mt-8 border-t border-white/10 pt-6">
                <p className="text-xs uppercase tracking-widest text-slate-500">Other services</p>
                <ul className="mt-4 space-y-2">
                  {others.filter((o) => o.slug !== slug).slice(0, 5).map((o) => (
                    <li key={o.id}>
                      <Link to={`/services/${o.slug}`} className="flex items-center justify-between text-sm text-slate-300 hover:text-vantage">
                        {o.title} <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="container-wv mt-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All services
          </Link>
        </div>
      </section>
      <ContactCTA />
    </>
  );
}
